/**
 * MedicationReviewModal v120.4 — Chat Dialogue Medication Review
 * 
 * Per-medication adherence tracking with gated progression:
 *   - Orella greeting bubble
 *   - Each med gets: SmallBubble intro → amber med card → question card
 *   - Then overall helpfulness + side effects
 *   - Non-adherent answers show detail textbox
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, Check, X } from 'lucide-react'
import { SmallOrellaBubble, AnimatedCard, ChatKeyframes } from '../../components/OrellaChatHeader'
import orellaOIcon from '../../assets/logos/orella_o_icon.png'
import { useI18n } from '../../i18n'
import { DIALOGUE_BUBBLE, DIALOGUE_PAD } from '../../components/agent/dialogueBubble'

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'
const GREEN = '#66D170'
const GREEN_DARK = '#16A34A'

function InlineBubble({ message, visible, delay = 0, onComplete }) {
  const { t } = useI18n()
  const [showLogo, setShowLogo] = useState(false)
  const [showBubble, setShowBubble] = useState(false)
  const [showDots, setShowDots] = useState(false)
  const [showText, setShowText] = useState(false)
  const calledRef = useRef(false)

  useEffect(() => {
    if (!visible) return
    const t = []
    t.push(setTimeout(() => setShowLogo(true), delay + 200))
    t.push(setTimeout(() => { setShowBubble(true); setShowDots(true) }, delay + 400))
    t.push(setTimeout(() => { 
      setShowDots(false); setShowText(true)
      if (!calledRef.current) { calledRef.current = true; onComplete?.() }
    }, delay + 1200))
    return () => t.forEach(clearTimeout)
  }, [visible, delay])

  if (!visible) return null

  return (
    <div className="px-4 pt-3 pb-1">
      <div className="flex items-start gap-2.5">
        <img src={orellaOIcon} alt="O"
          className="w-9 h-9 rounded-[10px] flex-shrink-0 object-contain transition-all duration-[400ms]"
          style={{ opacity: showLogo ? 1 : 0, transform: showLogo ? 'scale(1)' : 'scale(0.75)' }} />
        <div className={`flex-1 ${DIALOGUE_BUBBLE} ${DIALOGUE_PAD} transition-all duration-[400ms]`}
          style={{ opacity: showBubble ? 1 : 0, transform: showBubble ? 'translateY(0) scale(1)' : 'translateY(4px) scale(0.95)' }}>
          {showDots && !showText && (
            <span className="inline-flex items-center gap-1 h-5">
              {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite]" style={{ animationDelay: `${i*0.2}s` }} />)}
            </span>
          )}
          {showText && <p className="text-[15.5px] font-medium text-[#15354F] animate-[fadeIn_0.3s_ease]" style={{ lineHeight: 1.55 }}>{message}</p>}
        </div>
      </div>
    </div>
  )
}

export default function MedicationReviewModal({ open, onClose, onSubmit, medications, aiQuestions }) {
  const { t } = useI18n()
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState({})
  const [details, setDetails] = useState({})
  const scrollRef = useRef(null)

  useEffect(() => { if (!open) { setStep(0); setAnswers({}); setDetails({}) } }, [open])

  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 600)
    }
  }, [step])

  if (!open) return null

  // Build questions: per-med adherence + helpfulness + side effects
  const meds = (medications || []).map(m => typeof m === 'string' ? { name: m, dose: '' } : { name: m.name || m.description || 'Medication', dose: m.dose || m.dosage || '' })
  
  const staticQuestions = [
    ...meds.map(m => ({
      key: `adherence_${m.name.replace(/\s+/g, '_').toLowerCase()}`,
      question: t('medReview.howOftenUsing', { name: m.name }),
      medName: m.name, medDose: m.dose,
      options: ['Every day', 'Most days', 'Sometimes', 'Rarely', 'Not at all'],
      neg: [0, 1],
      detailPrompt: "Why haven't you been able to take it regularly?",
      detailPlaceholder: "e.g. Forgot some days, ran out early..."
    })),
    { key: 'helpfulness', question: 'Overall, how helpful has your treatment been?', options: ['Very helpful', 'Somewhat helpful', 'Not sure yet', 'Not helpful'], neg: [0, 1], detailPrompt: "What hasn't been working?", detailPlaceholder: "e.g. Symptoms haven't improved much..." },
    { key: 'side_effects', question: 'Any side effects from your medications?', options: ['None', 'Mild', 'Moderate', 'Severe'], neg: [0], detailPrompt: 'Describe the side effects:', detailPlaceholder: 'e.g. Nausea, headaches, dizziness...' },
  ]

  // v164.0.0: Append AI-generated medication-specific questions after static ones
  const aiAdapted = (aiQuestions || []).map((q, i) => ({
    key: q.id || `mq_ai_${i}`,
    question: q.text,
    options: q.type === 'free-text' ? [] : (q.options || []),
    neg: q.type === 'free-text' ? [] : [0, 1],
    type: q.type,
    detailPrompt: q.type === 'free-text' ? null : 'Tell us more:',
    detailPlaceholder: 'Describe...',
  }))

  const questions = [...staticQuestions, ...aiAdapted]

  const answeredCount = Object.keys(answers).length
  const allDone = answeredCount === questions.length

  const handleAnswer = (qKey, optIndex, isNeg) => {
    setAnswers(a => ({ ...a, [qKey]: optIndex }))
    if (isNeg) {
      const qIdx = questions.findIndex(q => q.key === qKey)
      setTimeout(() => setStep(s => Math.max(s, qIdx + 2)), 400)
    }
  }

  const advancePast = (qKey) => {
    const qIdx = questions.findIndex(q => q.key === qKey)
    setStep(s => Math.max(s, qIdx + 2))
  }

  const handleSubmit = () => {
    const result = {}
    questions.forEach(q => {
      if (answers[q.key] !== undefined) {
        if (q.type === 'free-text') {
          result[q.key] = details[q.key] || ''
        } else {
          result[q.key] = q.options[answers[q.key]]
          if (details[q.key]) result[`${q.key}_detail`] = details[q.key]
        }
      }
    })
    onSubmit(result)
  }

  const getIntro = (qi) => {
    if (questions[qi].medName) return t('medReview.howHasBeenGoing', { name: questions[qi].medName })
    if (qi === questions.length - 1) return 'Last one.'
    return 'And one more.'
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-[#F8FAFC]" style={{ animation: 'fadeSlideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}>
      <ChatKeyframes />
      <style>{`@keyframes fadeSlideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }`}</style>

      {/* ★ v242.87.0 — same defect as TF b35 #28 ("Overlap in menu"), same file
          shape, third copy. A portaled full-screen column owns the whole screen,
          so MobileLayout's inset never reaches it and it must paint its own or
          the title sits under the clock. Canonical expression, byte-identical to
          CheckInModal.jsx / SymptomCheckModal.jsx. Gated by
          scripts/check-safe-area.mjs. */}
      <div className="flex items-center gap-2.5 px-4 pb-3 border-b border-gray-100 bg-white flex-shrink-0"
           data-safe-top="1"
           style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
        <button onClick={onClose} className="p-1"><ArrowLeft size={22} style={{ color: NAVY }} /></button>
        <span className="text-[15px] font-bold" style={{ color: NAVY }}>{t('medReview.medicationReview')}</span>
        <button onClick={onClose} className="p-1 ml-auto"><X size={22} className="text-gray-400" /></button>
      </div>

      {/* Scrollable Content */}
      <div ref={scrollRef} className="flex-1 overflow-auto pb-4">
        <InlineBubble visible={true} delay={0} onComplete={() => setStep(1)}
          message={t('medReview.letsReviewMeds')} />

        {questions.map((q, qi) => {
          const qStep = qi + 1
          const isActive = step >= qStep
          const answered = answers[q.key] !== undefined
          const isNeg = answered && q.neg.includes(answers[q.key])
          const needsDetail = answered && !isNeg

          if (!isActive) return null

          return (
            <div key={q.key}>
              <SmallOrellaBubble visible={true} delay={qStep === step ? 200 : 0} message={getIntro(qi)} />

              {/* Medication info pill */}
              {q.medDose !== undefined && (
                <AnimatedCard visible={true} delay={qStep === step ? 500 : 0}>
                  <div className="flex items-center gap-2 rounded-[10px] bg-amber-50 px-3 py-2 mb-0.5">
                    <div className="w-7 h-7 rounded-[7px] bg-white flex items-center justify-center">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2"><path d="M10.5 1.5l-8 8a5 5 0 0 0 7 7l8-8a5 5 0 0 0-7-7z"/><path d="M7.5 10.5l5-5"/></svg>
                    </div>
                    <div>
                      <span className="text-[12px] font-bold text-amber-800">{q.medName}</span>
                      {q.medDose && <span className="text-[10px] text-amber-600 block">{q.medDose}</span>}
                    </div>
                  </div>
                </AnimatedCard>
              )}

              {/* Question card */}
              <AnimatedCard visible={true} delay={qStep === step ? (q.medDose !== undefined ? 650 : 500) : 0}>
                <div className={`rounded-[14px] border-2 p-4 mb-0.5 ${answered ? 'border-green-200' : 'border-gray-200'} bg-white`}>
                  <p className="text-[13px] font-bold text-gray-800 mb-3">{q.question}</p>

                  {q.type === 'free-text' ? (
                    /* v164.0.0: Free-text question from AI */
                    <div>
                      <textarea
                        value={details[q.key] || ''} onChange={e => {
                          setDetails(d => ({ ...d, [q.key]: e.target.value }))
                          if (!answered && e.target.value.length > 0) setAnswers(a => ({ ...a, [q.key]: 'free-text' }))
                          if (e.target.value.length === 0) setAnswers(a => { const n = { ...a }; delete n[q.key]; return n })
                        }}
                        placeholder={t('medReview.typeResponseHere')}
                        className="w-full h-20 border-[1.5px] border-gray-200 rounded-[10px] p-2.5 text-[12px] text-gray-700 resize-none focus:border-blue-300 focus:outline-none" />
                      {answered && (
                        <button onClick={() => advancePast(q.key)}
                          className="mt-1.5 text-[11px] font-semibold border-none bg-transparent cursor-pointer" style={{ color: BLUE }}>
                          Continue →
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {q.options.map((opt, oi) => (
                          <button key={oi} onClick={() => handleAnswer(q.key, oi, q.neg.includes(oi))}
                            className="px-3.5 py-2 rounded-xl text-[12px] font-semibold border-2 transition-all"
                            style={{
                              borderColor: answers[q.key] === oi ? BLUE : '#E2E8F0',
                              background: answers[q.key] === oi ? ICE_BLUE : 'white',
                              color: answers[q.key] === oi ? NAVY : '#64748B',
                            }}>
                            {opt}
                          </button>
                        ))}
                      </div>

                      {needsDetail && (
                        <div className="mt-3 animate-[fadeIn_0.3s_ease]">
                          <p className="text-[11px] font-semibold text-gray-500 mb-1.5">{q.detailPrompt}</p>
                          <textarea value={details[q.key] || ''} onChange={e => setDetails(d => ({ ...d, [q.key]: e.target.value }))}
                            placeholder={q.detailPlaceholder}
                        className="w-full h-16 border-[1.5px] border-gray-200 rounded-[10px] p-2.5 text-[12px] text-gray-700 resize-none focus:border-blue-300 focus:outline-none" />
                      <button onClick={() => advancePast(q.key)}
                        className="mt-1 text-[11px] font-semibold border-none bg-transparent cursor-pointer" style={{ color: BLUE }}>
                        Continue →
                      </button>
                    </div>
                  )}
                    </>
                  )}
                </div>
              </AnimatedCard>
            </div>
          )
        })}

        {allDone && (
          <SmallOrellaBubble visible={true} delay={300} message={t('medReview.thanksDoctorReview')} />
        )}
      </div>

      {/* Bottom Button */}
      <div className="p-4 pb-6 border-t border-gray-100 bg-white flex-shrink-0">
        <button onClick={handleSubmit} disabled={!allDone}
          className="w-full py-3.5 rounded-[14px] text-[14px] font-bold flex items-center justify-center gap-2 transition-all"
          style={{
            background: allDone ? `linear-gradient(135deg, ${NAVY}, #1E4A6E)` : '#E2E8F0',
            color: allDone ? '#fff' : '#94A3B8',
          }}>
          <Check size={16} /> {t('medReview.saveResponses', 'Save Responses')}
        </button>
      </div>
    </div>,
    document.body
  )
}
